import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export default function USAExperienceHeader() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const isFar = (top1, left1, top2, left2, minDistance = 15) => {
    const dx = top1 - top2;
    const dy = left1 - left2;
    return Math.sqrt(dx * dx + dy * dy) > minDistance;
  };

  const generateStarPositions = (count) => {
    const positions = [];
    while (positions.length < count) {
      const top = Math.random() * 80;
      const left = Math.random() * 80;
      const isTooClose = positions.some(pos => !isFar(top, left, pos.top, pos.left));
      if (!isTooClose) {
        positions.push({ top, left });
      }
    }
    return positions;
  };

  const starPositions = useMemo(() => generateStarPositions(6), []);

  const directions = matches
    ? [
        'translate(40px, -50px)',
        'translate(-50px, 40px)',
        'translate(30px, 60px)',
        'translate(-40px, -30px)',
        'translate(60px, 40px)',
        'translate(-30px, 60px)',
      ]
    : [
        'translate(200px, -250px)',
        'translate(-240px, 180px)',
        'translate(160px, 300px)',
        'translate(-200px, -160px)',
        'translate(260px, 200px)',
        'translate(-180px, 320px)',
      ];

  return (
    <div style={{ position: 'relative', overflowX: 'hidden' }}>
      {/* Star Animation Styles */}
      <style>
        {starPositions.map((_, index) => ` 
          @keyframes moveAround${index} {
            0% { transform: translate(0, 0); }
            50% { transform: ${directions[index % directions.length]}; }
            100% { transform: translate(0, 0); }
          }
        `).join('\n')}
      </style>

      {/* Star Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}>
        {starPositions.map(({ top, left }, index) => (
          <img
            key={index}
            src="https://dbqa318yoj3nn.cloudfront.net/Star_org.png"
            alt="star"
            style={{
              position: 'absolute',
              top: `${top}%`,
              left: `${left}%`,
              width: matches ? '20px' : '40px',
              animation: `moveAround${index} ${15 + Math.random() * 10}s ease-in-out infinite`,
            }}
          />
        ))}
      </div>

      {/* Globe Section */}
      <div style={{
        position: 'relative',
        display: 'flex',
        flexDirection: matches ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url("https://dbqa318yoj3nn.cloudfront.net/globe_bg.png")`,
        backgroundSize: 'cover',
        padding: matches ? '8px' : '24px',
        minHeight: matches ? '40vh' : '70vh',
        width: '100%',
      }}>
       
          <div>
            <div style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              marginTop: '20%',
              fontSize:matches?"64px": '128px',
              lineHeight: '100%',
              textAlign: 'center',
              textTransform: 'capitalize',
              color: '#5B21B6',
              wordWrap: 'break-word',
              marginBottom: '8px',
            }}>
              Global
            </div>
            <div style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize:matches?"64px": '128px',
              lineHeight: '100%',
              textAlign: 'center',
              textTransform: 'capitalize',
              color: '#5B21B6',
              wordWrap: 'break-word',
              marginBottom:matches?"":'25%'
            }}>
              Exposure
            </div>
          </div>

        {/* Globe Image */}
        <div style={{
          position: matches ? 'relative' : 'absolute',
          top: matches ? '0px' : '-50px',
          left: matches ? 'auto' : '3%',
        }}>
          <img src="https://dbqa318yoj3nn.cloudfront.net/23.png" style={{ maxWidth: '350px', height: 'auto' }} alt="Globe" />
        </div>

        {/* Pencil Image Animation */}
        <motion.div
          style={{
            position: matches ? 'relative' : 'absolute',
            bottom: matches ? 'auto' : '60px',
            right: matches ? 'auto' : '100px',
            marginTop: matches ? '10px' : '0',  // Reduced margin
          }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, -10, 10, -10, 10, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatType: 'loop',
            ease: 'easeInOut',
          }}
        >
          <img
            src="https://dbqa318yoj3nn.cloudfront.net/right_pencil.png"
            alt="pencil"
            style={{ width: matches ? '150px' : '250px', maxWidth: '100%' }}  // Adjusted size
          />
        </motion.div>
      </div>

      {/* Skill-Based Test Section */}
      <div
        style={{
          width: '100%',
          height: matches ? 'auto' : '20vh',
          backgroundColor: 'white',
          position: 'relative',
          top: matches ? '-30px' : '-80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: matches ? '16px 0' : '0',  // Reduced padding
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: matches ? '90%' : '80%',
            backgroundColor: '#A44DFF',
            padding: '16px',
            borderRadius: '16px',
            color: 'white',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              color: '#FFD700',
              fontSize: matches ? '1rem' : '1.5rem',  // Reduced font size
              fontWeight: 'bold',
              marginBottom: '8px',  // Reduced margin
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            Skill-Based Test
          </div>

          <div
            style={{
              display: 'flex',
              fontFamily: 'Popins',
              flexDirection: matches ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'center',
              gap: matches ? '8px' : '16px',  // Reduced gap
            }}
          >
            {/* Section 1 */}
            <div style={{  fontFamily: 'Popins',padding: '0 16px', width: matches ? '100%' : '30%' }}>
              <p>
                A skill-based test will be conducted among all the students enrolled in NextGen Innovators Program.
              </p>
            </div>

            {/* Divider */}
            <div
              style={{
                width: matches ? '60%' : '4px',
                height: matches ? '4px' : '120px',
                background: matches
                  ? 'linear-gradient(to right, rgba(180, 100, 255, 0), rgba(255, 255, 255, 1), rgba(180, 100, 255, 0))'
                  : 'linear-gradient(to bottom, rgba(180, 100, 255, 0), rgba(255, 255, 255, 1), rgba(180, 100, 255, 0))',
                borderRadius: '2px',
                margin: matches ? '8px 0' : '0 16px',  // Reduced margin
                boxShadow: '0 0 12px 4px rgba(180, 100, 255, 0.6)',
                alignSelf: 'center',
              }}
            />

            {/* Section 2 */}
            <div style={{  fontFamily: 'Popins', padding: '0 16px', width: matches ? '100%' : '30%' }}>
              <p>
                Based on the results, top 500 nominations from the program will go through an interview process.
              </p>
            </div>

            {/* Divider */}
            <div
              style={{
                width: matches ? '60%' : '4px',
                height: matches ? '4px' : '120px',
                background: matches
                  ? 'linear-gradient(to right, rgba(180, 100, 255, 0), rgba(255, 255, 255, 1), rgba(180, 100, 255, 0))'
                  : 'linear-gradient(to bottom, rgba(180, 100, 255, 0), rgba(255, 255, 255, 1), rgba(180, 100, 255, 0))',
                borderRadius: '2px',
                margin: matches ? '8px 0' : '0 16px',  // Reduced margin
                boxShadow: '0 0 12px 4px rgba(180, 100, 255, 0.6)',
                alignSelf: 'center',
              }}
            />

            {/* Section 3 */}
            <div style={{ fontFamily: 'Popins', padding: '0 16px', width: matches ? '100%' : '30%' }}>
              <p>
                The top 40 students from this interview process will earn the opportunity to visit the USA.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
